import { Suspense, useEffect, useRef } from 'react'
import { NavLink, Outlet, useLocation } from 'react-router-dom'
import {
  BarChart3,
  Calendar,
  Loader2,
  Palmtree,
  Settings,
  Users,
} from 'lucide-react'

import Header from '@/components/layout/Header'
import RouteErrorBoundary from '@/components/layout/RouteErrorBoundary'
import { cn } from '@/lib/cn'
import { useCanManagePersonnel } from '@/lib/acl'

const calendarTab = { to: '/admin/calendar', label: 'Kalendarz', icon: Calendar }
const reportTab = { to: '/admin/report', label: 'Raport', icon: BarChart3 }
const vacationTab = { to: '/admin/vacation', label: 'Urlopy', icon: Palmtree }
const usersTab = { to: '/admin/users', label: 'Pracownicy', icon: Users }
const settingsTab = { to: '/admin/settings', label: 'Ustawienia', icon: Settings }

export default function AdminLayout() {
  const canManage = useCanManagePersonnel()
  const location = useLocation()
  const navRef = useRef<HTMLDivElement>(null)

  // Po M6c-2 wszyscy z canManagePersonnel (ADMIN + Manager + Księgowa) widzą wszystkie
  // 5 zakładek. Per-page guardy ograniczają operacje na poziomie handler'ów backend.
  const tabs = canManage
    ? [calendarTab, reportTab, vacationTab, usersTab, settingsTab]
    : []

  // Kalendarz potrzebuje pełnej szerokości (5 kolumn dni × hojny padding na 4K).
  // Tabela zespołu w raporcie ma po dołożeniu kolumn urlopowych 14 kolumn —
  // w max-w-5xl (1024px) zjeżdżała w poziomy scroll przy każdym otwarciu.
  // Karta pojedynczego pracownika (/admin/report/:id) zostaje wąska — to widok
  // czytelniczy, nie arkusz. Reszta (users, settings, vacation) bez zmian.
  const isWideRoute =
    location.pathname.startsWith('/admin/calendar') ||
    location.pathname === '/admin/report'

  // Pasek zakładek dzieli szerokość i padding z treścią — inaczej na kalendarzu
  // nav kończył się na 1024 px, a treść szła do 1600 px, i krawędzie się nie
  // zgadzały (do tego 4 px rozjazdu na telefonie: px-4 w nav vs px-3 w main).
  const containerCls = isWideRoute
    ? 'max-w-screen-2xl 2xl:max-w-[1600px] 3xl:max-w-[2200px] px-3 sm:px-4 lg:px-4 3xl:px-6'
    : 'max-w-5xl px-4 sm:px-6'

  // Pasek scrolluje się w poziomie na telefonie, ale nigdy nie dosuwał aktywnej
  // zakładki — wchodząc w „Ustawienia" widziało się „Kalendarz | Raport…"
  // i trzeba było przesuwać palcem, żeby zobaczyć, gdzie się właściwie jest.
  useEffect(() => {
    const active = navRef.current?.querySelector('[aria-current="page"]')
    active?.scrollIntoView({ inline: 'nearest', block: 'nearest' })
  }, [location.pathname])

  return (
    <div className="min-h-[100svh] flex flex-col bg-bg-canvas">
      <Header />
      <nav className="bg-bg-surface border-b border-border-subtle">
        <div
          ref={navRef}
          className={cn(
            'mx-auto flex gap-1 overflow-x-auto overscroll-x-contain scrollbar-hide',
            containerCls,
          )}
        >
          {tabs.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'inline-flex items-center gap-2 px-4 py-3 text-sm border-b-2 transition whitespace-nowrap',
                  isActive
                    ? 'border-brand-accent text-text-primary'
                    : 'border-transparent text-text-muted hover:text-text-primary',
                )
              }
            >
              <Icon className="h-4 w-4" aria-hidden />
              {label}
            </NavLink>
          ))}
        </div>
      </nav>
      <main
        className={cn(
          'flex-1 w-full mx-auto py-6',
          containerCls,
          isWideRoute && 'lg:py-6 3xl:py-8',
        )}
      >
        {/* Klucz na ścieżce — po przejściu na inną zakładkę ekran awaryjny
            znika sam, zamiast zostawać do przeładowania strony. */}
        <RouteErrorBoundary key={location.pathname}>
          {/* Granica dla tras ładowanych leniwie (np. ustawienia geofencingu,
              które ciągną maplibre-gl). Jedna na cały panel wystarcza — obejmuje
              też zagnieżdżony Outlet w SettingsLayout. */}
          <Suspense
            fallback={
              <p className="text-sm text-text-muted inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Wczytywanie…
              </p>
            }
          >
            <Outlet />
          </Suspense>
        </RouteErrorBoundary>
      </main>
    </div>
  )
}
